import { Injectable } from '@angular/core';
import * as cryptoJS from 'crypto-js';
import * as KJUR from 'jsrsasign';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class JwtService {
  //private aesKey = environment.aesKey; // Environment Specific AES Key
  private publicKey: string | null = null;


  private certificate: string = '';




  constructor(private jwtHelper: JwtHelperService,
    private http: HttpClient
  ) {

    // set this first one if production otherwise the only other one
    // or update as new conditionality is required
    console.log("in JWY , is this prod", environment.production);


    // Production certificate is used for ALL environments (local + prod + ppe + staging).
    // The QA-only certificate has been retired; tokens are signed with the prod private key,
    // so every deployed environment must verify against this matching prod public cert.
    //NB: the Regex removes any spaces from the left side of the certificate because the right side is only line breaks, ^(\s+)
    const prodCertificate = `
      -----BEGIN CERTIFICATE-----
      MIICmjCCAYKgAwIBAgICBNIwDQYJKoZIhvcNAQELBQAwDjEMMAoGA1UEAwwDR0ZI
      MB4XDTI1MDIwMTEzNTI0NVoXDTI4MDIwMTEzNTI0NVowEjEQMA4GA1UEAwwHQ2hh
      dGJvdDCCASIwDQYJKoZIhvcNAQEBBQADggEPADCCAQoCggEBAJuSSKlXLXqzJ9qV
      QVLq5Ojfak7zOmN6sAvl5J56xS/YotAuVrBHrjq2JTOG1+0+EWzHI6MHL007Hs2c
      ns866NSWzO/TPbndwCh/Ur5DmoBqEPPKAamFDP+YkYqy0SU9Nl5k/sFpv6zu9bpS
      DIYrjQdt4pvP36aUdruKZYVPyt1eK4bOilfvuVh9UTMJ7gOt8F/byq53Bcg9tnF3
      yuMpBTMm0YwcG6ZqTRn+AWYzdGvOz7lZMYnzG9PMMNEPRTbfg2kv3Jt2i0G/WKAp
      CTooQRoqP7Abp8JtsF9wQrQEJ8wS8TFUqvqsQqvT1t+9d4bf3m15oc7FVQfX7vTa
      MXtfJ/MCAwEAATANBgkqhkiG9w0BAQsFAAOCAQEAbtJZYEqJhxbthaGCVTA1vmuP
      5yl7dNaNFkO7J8fnTGjDqmF3OWjBKR6m3omQ22mcJecuqA17p7sq1b9FV+aWZYsQ
      xeiVtHwvOJLj77mySzL+8RXY/qnALQ8o7ngcW00DwV0p5s7clgiEMDvS/NmEu/rf
      UuEmKzRF21RkgN1Av4dfVVzQlaAoM7FRC68MK3jJuGsQPjd2cq6r201IacLqM2Um
      Vp4n2DHd278TToj87mz0MnMZ6HEhFh+vJmZ7RQbBfTZBsPBJoKOsB4l7VdXKNsaN
      wO9hTi049OdqUBI9C4e9XhMg7RjUgjHHaDnyrihQ0ZgfarI+l9itmVPkPy6Kxw==
      -----END CERTIFICATE-----
      `.replaceAll("^(\s+)", "");

    this.certificate = prodCertificate;

  }




  /**
   * Load the public key from PEM file
   */
//   async loadPublicKey(): Promise<void> {
//     if (!this.publicKey) {
//       try {
//         const pemFile = await lastValueFrom(this.http.get(environment.publicKeyPath, { responseType: 'text' }));
//         this.publicKey = pemFile;
//       } catch (error) {
//         console.error('Failed to load public key:', error);
//         throw new Error('Public key loading failed');
//       }
//     }
//   }

  // Extract public key from the certificate
  private extractPublicKey(): string {
    try {

      //console.log("here is the cert:", this.certificate.trim());

      const x509 = new KJUR.X509();
      x509.readCertPEM(this.certificate.trim());
      const publicKey = x509.getPublicKey();
      const publicKeyPEM = KJUR.KEYUTIL.getPEM(publicKey);
      return publicKeyPEM;
    } catch (error) {
      console.error('Failed to extract public key:', error.message);
      throw new Error('Invalid certificate content');
    }
  }

  /**
   * Validate the JWT signature using the public key
   * https://github.com/kjur/jsrsasign
   * @param token JWT token
   * @returns true if valid, false otherwise
   */
  validateJwtSignature(token: string): boolean {
    try {
      this.publicKey = this.extractPublicKey();

      //console.log("Public Key ::: " + this.publicKey)

      // Verify the JWT signature
      const isValid = KJUR.jws.JWS.verifyJWT(token, this.publicKey, { alg: ['RS256'] });

      //const decoded = KJUR.jws.JWS.parse(token);
      //console.log('Decoded JWT:', decoded);

      console.log("Signature is valid ::: " + isValid);
      return isValid;
    } catch (error) {
      console.error('JWT validation failed:', error);
      return false;
    }
  }

  /**
   * Decrypt the AES encrypted "sub" claim in the JWT payload
   * @param token JWT token
   * @returns Decrypted "sub" claim or null if failed
   */
  extractEncryptedSubClaim(token: string): string | null {
    try {
      const decodedPayload = this.jwtHelper.decodeToken(token);

      if (!decodedPayload || !decodedPayload.sub) {
        console.error('Missing "sub" claim in JWT payload');
        return null;
      }

      // Extract encrypted "sub" claim
      const encryptedSub = decodedPayload.sub.trim();

      return encryptedSub;
    } catch (error) {
      console.error('Decryption failed:', error.message);
      return null;
    }
  }

  /**
   * Validate JWT and decrypt "sub" claim
   * @param token JWT token
   * @returns Decrypted "sub" claim or null if validation/decryption fails
   */
  processJwt(token: string): string | null {
    if (!this.validateJwtSignature(token)) {
      console.error('Invalid JWT signature');
      return null;
    }

    const encryptedSub = this.extractEncryptedSubClaim(token);
    if (!encryptedSub) {
      console.error('Failed to get "sub" claim');
    }

    return encryptedSub;
  }

  // decryptSubClaim(encryptedValue: string): string | null {
  //   try {
  //     // Extract IV from the first 16 characters of the encrypted value
  //     const iv = cryptoJS.enc.Utf8.parse(encryptedValue.substring(0, 16));

  //     // Decode the remaining part of the encrypted value as Base64
  //     const encryptedData = cryptoJS.enc.Base64.parse(encryptedValue.substring(16));

  //     // Initialize AES cipher with the key and IV
  //     const cipher = cryptoJS.AES.decrypt(
  //       { ciphertext: encryptedData } as cryptoJS.lib.CipherParams,
  //       cryptoJS.enc.Utf8.parse(environment.aesKey),
  //       { iv: iv, mode: cryptoJS.mode.CBC, padding: cryptoJS.pad.Pkcs7 }
  //     );

  //     // Convert the decrypted data to a UTF-8 string
  //     const original = cipher.toString(cryptoJS.enc.Utf8);
  //     //console.log(`Original Token Claim :: ${original}`);

  //     // Return the decrypted text
  //     return original;

  //   } catch (error) {
  //       console.error(`Decryption failed: ${error}`);
  //       return null;
  //     }
  // }
}
